import {
  REPORT_ITEM_LIMIT,
  REPORT_RENDERER_VERSION,
  REPORT_SCHEMA_VERSION,
  getSourceModuleForReportType,
  normalizeReportOrientation,
} from "./reportTypes";

const STORAGE_PREFIX = "reports";
const REPORT_STORE_EVENT = "electionmap:reports-store-changed";

function normalizeScopeValue(value) {
  return String(value || "").trim();
}

export function getReportStorageKey(userId, areaId) {
  const userKey = normalizeScopeValue(userId);
  const areaKey = normalizeScopeValue(areaId);
  if (!userKey || !areaKey) return "";
  return `${STORAGE_PREFIX}:${userKey}:${areaKey}`;
}

function emptyWorkspace(userId, areaId) {
  return {
    schemaVersion: REPORT_SCHEMA_VERSION,
    userId: normalizeScopeValue(userId),
    areaId: normalizeScopeValue(areaId),
    items: [],
    updatedAt: new Date().toISOString(),
  };
}

function safeClone(value, fallback = null) {
  try {
    return JSON.parse(JSON.stringify(value));
  } catch (_) {
    return fallback;
  }
}

function normalizeItem(item, index) {
  if (!item || typeof item !== "object") return null;
  const type = String(item.type || "").trim();
  const sourceModule = String(item.sourceModule || getSourceModuleForReportType(type) || "").trim();
  const id = String(item.id || "").trim();
  if (!id || !type || !sourceModule) return null;
  const snapshot = item.snapshot && typeof item.snapshot === "object" ? safeClone(item.snapshot, {}) : {};

  return {
    id,
    schemaVersion: Number(item.schemaVersion) || REPORT_SCHEMA_VERSION,
    rendererVersion: String(item.rendererVersion || REPORT_RENDERER_VERSION),
    type,
    sourceModule,
    areaId: item.areaId,
    areaName: String(item.areaName || ""),
    title: String(item.title || "Report"),
    note: String(item.note || ""),
    orientation: normalizeReportOrientation(type, item.orientation),
    includeInExport: item.includeInExport !== false,
    sortOrder: Number.isFinite(Number(item.sortOrder)) ? Number(item.sortOrder) : index,
    createdAt: String(item.createdAt || new Date().toISOString()),
    updatedAt: String(item.updatedAt || item.createdAt || new Date().toISOString()),
    snapshot,
  };
}

function normalizeWorkspace(raw, userId, areaId) {
  const base = emptyWorkspace(userId, areaId);
  if (!raw || typeof raw !== "object") return base;
  const items = (Array.isArray(raw.items) ? raw.items : [])
    .map(normalizeItem)
    .filter(Boolean)
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .slice(0, REPORT_ITEM_LIMIT)
    .map((item, index) => ({ ...item, sortOrder: index }));
  return {
    ...base,
    schemaVersion: Number(raw.schemaVersion) || REPORT_SCHEMA_VERSION,
    items,
    updatedAt: String(raw.updatedAt || base.updatedAt),
  };
}

export function readReportWorkspace(userId, areaId) {
  const storageKey = getReportStorageKey(userId, areaId);
  if (!storageKey || typeof window === "undefined" || !window.localStorage) {
    return emptyWorkspace(userId, areaId);
  }
  try {
    const raw = window.localStorage.getItem(storageKey);
    return normalizeWorkspace(raw ? JSON.parse(raw) : null, userId, areaId);
  } catch (_) {
    return emptyWorkspace(userId, areaId);
  }
}

function emitStoreChanged(storageKey) {
  if (typeof window === "undefined") return;
  try {
    window.dispatchEvent(new CustomEvent(REPORT_STORE_EVENT, { detail: { storageKey } }));
  } catch (_) {
    window.dispatchEvent(new Event(REPORT_STORE_EVENT));
  }
}

export function writeReportWorkspace(userId, areaId, workspace) {
  const storageKey = getReportStorageKey(userId, areaId);
  if (!storageKey || typeof window === "undefined" || !window.localStorage) return null;
  const normalized = normalizeWorkspace(workspace, userId, areaId);
  normalized.updatedAt = new Date().toISOString();
  window.localStorage.setItem(storageKey, JSON.stringify(normalized));
  emitStoreChanged(storageKey);
  return normalized;
}

export function subscribeReportWorkspace(callback) {
  if (typeof window === "undefined") return () => {};
  const onStore = () => callback && callback();
  const onStorage = (event) => {
    if (!event.key || event.key.startsWith(`${STORAGE_PREFIX}:`)) onStore();
  };
  window.addEventListener(REPORT_STORE_EVENT, onStore);
  window.addEventListener("storage", onStorage);
  return () => {
    window.removeEventListener(REPORT_STORE_EVENT, onStore);
    window.removeEventListener("storage", onStorage);
  };
}

function resequence(items) {
  return items.map((item, index) => ({ ...item, sortOrder: index }));
}

export function createReportItem({ type, sourceModule, areaId, areaName, title, note, orientation, snapshot }) {
  const now = new Date().toISOString();
  const id =
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : `report-${Date.now()}-${Math.random().toString(16).slice(2)}`;
  return normalizeItem(
    {
      id,
      schemaVersion: REPORT_SCHEMA_VERSION,
      rendererVersion: REPORT_RENDERER_VERSION,
      type,
      sourceModule: sourceModule || getSourceModuleForReportType(type),
      areaId,
      areaName,
      title,
      note,
      orientation: normalizeReportOrientation(type, orientation),
      includeInExport: true,
      sortOrder: 0,
      createdAt: now,
      updatedAt: now,
      snapshot: safeClone(snapshot, {}),
    },
    0
  );
}

export function addReportItem(userId, areaId, item) {
  const workspace = readReportWorkspace(userId, areaId);
  if (workspace.items.length >= REPORT_ITEM_LIMIT) {
    return { ok: false, error: `Report může obsahovat maximálně ${REPORT_ITEM_LIMIT} položek.` };
  }
  const nextItem = normalizeItem(item, workspace.items.length);
  if (!nextItem) return { ok: false, error: "Položku reportu se nepodařilo uložit." };
  const updated = writeReportWorkspace(userId, areaId, {
    ...workspace,
    items: resequence([...workspace.items, nextItem]),
  });
  return { ok: true, workspace: updated, item: nextItem };
}

export function updateReportItem(userId, areaId, itemId, patch) {
  const workspace = readReportWorkspace(userId, areaId);
  const now = new Date().toISOString();
  const updatedItems = workspace.items.map((item) => {
    if (item.id !== itemId) return item;
    const next = {
      ...item,
      title: patch.title !== undefined ? String(patch.title || "") : item.title,
      note: patch.note !== undefined ? String(patch.note || "") : item.note,
      includeInExport:
        patch.includeInExport !== undefined ? Boolean(patch.includeInExport) : item.includeInExport,
      orientation:
        patch.orientation !== undefined ? normalizeReportOrientation(item.type, patch.orientation) : item.orientation,
      updatedAt: now,
    };
    return next;
  });
  return writeReportWorkspace(userId, areaId, { ...workspace, items: resequence(updatedItems) });
}

export function deleteReportItem(userId, areaId, itemId) {
  const workspace = readReportWorkspace(userId, areaId);
  return writeReportWorkspace(userId, areaId, {
    ...workspace,
    items: resequence(workspace.items.filter((item) => item.id !== itemId)),
  });
}

export function moveReportItem(userId, areaId, itemId, direction) {
  const workspace = readReportWorkspace(userId, areaId);
  const index = workspace.items.findIndex((item) => item.id === itemId);
  if (index < 0) return workspace;
  const targetIndex = direction === "up" ? index - 1 : index + 1;
  if (targetIndex < 0 || targetIndex >= workspace.items.length) return workspace;
  const items = workspace.items.slice();
  const tmp = items[index];
  items[index] = items[targetIndex];
  items[targetIndex] = tmp;
  return writeReportWorkspace(userId, areaId, { ...workspace, items: resequence(items) });
}

